import axiosInstance from './interceptor.tsx'
import {
  ProposalStatus,
  CreateProposalDto,
  ProposalCreationResponse,
  FileUploadResponse,
  FileType,
  Proposal,
  CreateBidDto,
  BidCreationResponse,
  IssuerProposalOverviewDto,
  BuyerProposalDetailDto,
  BuyerProposalOverviewDto,
  ScheduleType,
} from '../components/shared/types'
import FileSaver from 'file-saver'

export const ProposalsApi = {
  createProposal: async (proposalData: CreateProposalDto): Promise<string> => {
    const response = await axiosInstance.post<ProposalCreationResponse>(
      '/proposals',
      proposalData
    )
    return response.data.proposalId
  },

  createBids: async (
    bidData: CreateBidDto[]
  ): Promise<BidCreationResponse[]> => {
    const response = await axiosInstance.post<BidCreationResponse[]>(
      '/proposals/bids',
      bidData
    )
    return response.data
  },

  getBuyerProposals: async (): Promise<BuyerProposalOverviewDto[]> => {
    const response =
      await axiosInstance.get<BuyerProposalOverviewDto[]>('/proposals/buyer')
    return response.data
  },

  getBuyerProposal: async (
    proposalId: string
  ): Promise<BuyerProposalDetailDto> => {
    const response = await axiosInstance.get<BuyerProposalDetailDto>(
      `/proposals/buyer/${proposalId}`
    )
    return response.data
  },

  uploadFile: async (file: File): Promise<FileUploadResponse> => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axiosInstance.post<FileUploadResponse>(
        '/proposals/files/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

      return response.data
    } catch (error) {
      const axiosError = error as { response?: { data?: FileUploadResponse } }
      if (axiosError.response?.data) {
        return axiosError.response.data
      }
      // Fallback error response if we don't have a structured error from the server
      return {
        fileId: null,
        fileName: file.name,
        success: false,
        errorMessage:
          error instanceof Error ? error.message : 'Unknown upload error',
      }
    }
  },

  deleteFile: async (fileId: string): Promise<boolean> => {
    await axiosInstance.delete(`/proposals/files/${fileId}`)
    return true
  },

  downloadFile: async (fileId: string, fileName: string): Promise<boolean> => {
    const response = await axiosInstance.get(`/proposals/files/${fileId}`, {
      responseType: 'blob',
    })
    FileSaver(response.data as Blob, fileName, { autoBom: false })
    return true
  },

  getAcceptedFileTypes: async (): Promise<FileType[]> => {
    const response = await axiosInstance.get<FileType[]>(
      '/proposals/files/types'
    )
    return response.data
  },

  updateProposalStatus: async (
    proposalId: string,
    status: ProposalStatus
  ): Promise<boolean> => {
    await axiosInstance.put(`/proposals/buyer/${proposalId}/status/${status}`)
    return true
  },

  createSelectedBids: async (
    selectedBids: {
      scenarioId: string
      issuerTenantId: string
      rate: number
      treasuryRate: number
      amount: number
      wamYears: number | null
      partialMaturities: {
        paymentDate: Date | string | null
        amount: number
        inputType: string
      }[]
    }[]
  ): Promise<boolean> => {
    await axiosInstance.post('/proposals/bids/selected', selectedBids)
    return true
  },

  getIssuerProposals: async (): Promise<IssuerProposalOverviewDto[]> => {
    const response =
      await axiosInstance.get<IssuerProposalOverviewDto[]>('/proposals/issuer')
    return response.data
  },

  getIssuerProposal: async (proposalId: string): Promise<Proposal> => {
    const response = await axiosInstance.get<Proposal>(
      `/proposals/issuer/${proposalId}`
    )
    return response.data
  },

  // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
  getLatestScheduleType: async (): Promise<ScheduleType> => {
    try {
      const response = await axiosInstance.get<{ scheduleType: ScheduleType }>(
        '/proposals/latest-schedule-type'
      )
      return response.data.scheduleType
    } catch (error) {
      console.error('Error fetching latest schedule type:', error)
      // Default to DEPOSIT if API call fails
      return ScheduleType.DEPOSIT
    }
  },

  // Windsurf generated
  updateProposalDescription: async (
    proposalId: string,
    description: string
  ): Promise<boolean> => {
    await axiosInstance.patch(`/proposals/buyer/${proposalId}/description`, {
      description,
    })
    return true
  },
}
